import {
  withAlpha,
  type AppearanceStyle,
  type NoticeAppearanceColors,
} from "../shared/appearance";
import type { Style, StyleMap, StylePatch } from "./types";

/**
 * Shallow per-key merge: each patched key keeps the classic properties it does
 * not override. Generic, so a surface whose style keys copy the modal's
 * (assisted) takes the same patches; a patched key it lacks is added unused.
 */
export const mergeNoticeStyles = <T extends StyleMap>(
  base: T,
  patch: StylePatch,
): T => {
  const merged: Record<string, Style> = { ...base };
  for (const [key, style] of Object.entries(patch)) {
    merged[key] = { ...merged[key], ...style };
  }
  return merged as T;
};

/** Drops unset keys, so a patch never overwrites a classic value with nothing. */
const defined = (style: Style): Style =>
  Object.fromEntries(
    Object.entries(style).filter(([, value]) => value !== undefined),
  );

const definedPatch = (patch: StylePatch): StylePatch =>
  Object.fromEntries(
    Object.entries(patch)
      .map(([key, style]) => [key, defined(style ?? {})] as const)
      .filter(([, style]) => Object.keys(style).length > 0),
  );

/**
 * The admin's palette painted onto classic's surfaces. Classic hardcodes white
 * on every section, so a dark palette has to reach each one; a colour the
 * admin left unset keeps classic's own value.
 */
const palettePatch = (colors: NoticeAppearanceColors): StylePatch => {
  const surface = colors.surface ?? colors.background;
  const section = { backgroundColor: colors.background };
  const edge = colors.border && `1px solid ${colors.border}`;
  return definedPatch({
    overlay: { backgroundColor: colors.overlay },
    modal: { backgroundColor: colors.background },
    topSection: section,
    headerStack: section,
    bottomSection: section,
    actionButtonRow: section,
    actionButtonStack: section,
    title: { color: colors.heading },
    subTitle: { color: colors.heading },
    optionTitle: { color: colors.heading },
    privacyText: { color: colors.text },
    dataElementText: { color: colors.text },
    optionDescription: { color: colors.muted_text },
    confirmHint: { color: colors.muted_text },
    link: { color: colors.link },
    topRight: { border: edge },
    languageDropdown: { backgroundColor: surface, border: edge },
    languageItem: {
      backgroundColor: surface,
      color: colors.text,
      borderBottom: edge,
    },
    otpGatePanel: { background: surface, border: edge },
    otpGateTitle: { color: colors.heading },
    otpGateDescription: { color: colors.muted_text },
    otpGateDigit: {
      background: colors.background,
      border: edge,
      color: colors.heading,
    },
  });
};

/** Flat and quiet: hairline edges, no shadows, tight corners. */
const minimalPatch = (colors: NoticeAppearanceColors): StylePatch => {
  const hairline = `1px solid ${colors.border ?? "#e4e7ec"}`;
  return {
    modal: { boxShadow: "none", border: hairline },
    languageDropdown: { boxShadow: "none" },
    optionItem: { paddingBottom: "10px", borderBottom: hairline },
    button: { boxShadow: "none", fontWeight: 500 },
    otpGatePanel: { borderRadius: "4px" },
  };
};

/** Rounded and friendly: brand-washed panels and a soft lifted card. */
const softPatch = (accent: string): StylePatch => ({
  modal: { boxShadow: "0 24px 48px -12px rgba(16, 24, 40, 0.22)" },
  optionItem: {
    padding: "12px 14px",
    borderRadius: "16px",
    backgroundColor: withAlpha(accent, 0.06),
  },
  languageDropdown: { borderRadius: "16px" },
  button: { fontWeight: 600 },
  otpGatePanel: { borderRadius: "16px", background: withAlpha(accent, 0.06) },
});

const variantPatch = (
  style: AppearanceStyle,
  colors: NoticeAppearanceColors,
  accent: string,
): StylePatch => {
  if (style === "minimal") {
    return minimalPatch(colors);
  }
  if (style === "soft") {
    return softPatch(accent);
  }
  return {};
};

/**
 * Classic's styles with the admin's palette and, for minimal or soft, their
 * shape on top. Glass builds its own skin over this, so the palette reaches it
 * too wherever glass does not paint the surface itself.
 */
export const buildVariantStyles = <T extends StyleMap>(
  base: T,
  style: AppearanceStyle,
  colors: NoticeAppearanceColors,
  accent: string,
): T =>
  mergeNoticeStyles(
    mergeNoticeStyles(base, palettePatch(colors)),
    variantPatch(style, colors, accent),
  );
